<template>
    <v-card class="scrollable-container pb-2 elevation-5">
        <v-card-title>
            <data-search-bar
                :title="title"
                :help-href="helpUrl"
                :options="dataSearchBarOptions"
                :is-filters-active="isFiltersActive"
                show-filters
                @filter="onFilterClick"
                @change="onDataSearchBarChange"
                @refresh="onRefreshClick"
            >
                <template v-slot:below-title>
                    <v-col>
                        <filter-chips
                            v-show="currentTab.name === 'auditLogs'"
                            :items="auditLogsFilterChips"
                            @change="onFilterChipsChange"
                        ></filter-chips>
                        <filter-chips
                            v-show="currentTab.name === 'venueEvents'"
                            :items="venueEventsFilterChips"
                            @change="onFilterChipsChange"
                        ></filter-chips>
                    </v-col>
                </template>
                <template v-slot:second-column>
                    <v-col cols="auto" class="px-5">
                        <v-sheet elevation="2" class="chart-slot pa-2">
                            <device-status-chart></device-status-chart>
                        </v-sheet>
                    </v-col>
                </template>
                <template v-slot:disclaimer>
                    <div class="pt-1 text-body-2 font-italic">
                        Last Update {{ lastHealthPollDate }} - See Device detail
                        pages for current status
                    </div>
                </template>
            </data-search-bar>
        </v-card-title>
        <v-card-text class="scrollable-content pb-2">
            <v-sheet elevation="5" class="fill-height scrollable-container">
                <v-tabs
                    v-model="tab"
                    grow
                    class="thin-tabs px-2 mb-2"
                    @change="onTabChange"
                >
                    <v-tab v-show="showAuditLogs">
                        <v-icon class="mx-2">{{ icons.viewList }}</v-icon>
                        <span>{{ tabItems[0].label }}</span>
                    </v-tab>
                    <v-tab v-show="showVenueEvents">
                        <v-icon class="mx-2">{{ icons.viewList }}</v-icon>
                        <span>{{ tabItems[1].label }}</span>
                    </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab" class="px-2 scrollable-content">
                    <v-tab-item>
                        <!-------- Auditlogs table --------->
                        <data-table-view
                            :headers="alTableFields"
                            :items="auditLogs"
                            :sort-by="sortBy"
                            :sort-desc="sortDesc"
                            :page="page"
                            :items-per-page="itemsPerPage"
                            :loading="isDataLoading"
                            :zebra-stripping="alZebraStripping"
                            :computed-fields="alComputedFields"
                            :show-select="false"
                            allow-export
                            class="audit-logs-table"
                            @update-sort-by="onUpdateSortBy"
                            @export="onExportDataClick"
                        >
                        </data-table-view>
                    </v-tab-item>
                    <v-tab-item>
                        <!--------- Venue Events table -------->
                        <data-table-view
                            :headers="tableFields"
                            :items="venueEvents"
                            :sort-by="sortBy"
                            :sort-desc="sortDesc"
                            :page="page"
                            :items-per-page="itemsPerPage"
                            :loading="isDataLoading"
                            :zebra-stripping="zebraStripping"
                            :computed-fields="computedFields"
                            :show-select="false"
                            allow-export
                            class="venue-events-table"
                            @update-sort-by="onUpdateSortBy"
                            @export="onExportDataClick"
                        >
                            <template v-slot:tableButtons="{ item }">
                                <span class="px-3">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon
                                                color="accent"
                                                cy-data="view-row"
                                                @click="
                                                    onVenueEventClick(
                                                        item,
                                                        $event
                                                    )
                                                "
                                                v-on="on"
                                                >{{ icons.viewItem }}
                                            </v-icon>
                                        </template>
                                        <span class="text-caption"
                                            >Event details</span
                                        >
                                    </v-tooltip>
                                </span>
                            </template>
                        </data-table-view>
                    </v-tab-item>
                </v-tabs-items>
            </v-sheet>
            <component
                :is="exportDialogComponent"
                title="Export to CSV: Activities"
                :entity-name="exportEntityName"
                :data-source-params="exportParams"
                :output-fields="dataExportFields"
                :output-file-name="dataExportFileName"
                @close="onExportDialogClose"
            ></component>
        </v-card-text>
        <activity-filters
            v-model="showVenueEventsFilters"
            title="Venue Events Filters"
            :filters="filters"
            @apply="onApplyFilters"
        ></activity-filters>
        <audit-logs-filters
            v-model="showAuditLogsFilters"
            title="Audit Logs Filters"
            :filters="filters"
            @apply="onApplyFilters"
        ></audit-logs-filters>
        <event-dialog
            :show="showEventDialog"
            :item="selectedEvent"
            @close="onCloseEvent"
        ></event-dialog>
    </v-card>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import DataSearchBar from '@/components/Data/DataSearchBar';
import DataTableView from '@/components/Data/DataTableView';
import CsvDataExporter from '@/components/Export/CsvDataExporter';
import FilterChips from '@/components/Filters/FilterChips';
import ActivityFilters from './filters/ActivityFilters';
import AuditLogsFilters from './filters/AuditLogsFilters';
import DeviceStatusChart from '../devices/components/DeviceStatusChart';
import { isEmpty, isEmptyValues } from '@/common/helpers';
import EventDialog from './EventDialog';

export default {
    name: 'ActivitiesPage',
    components: {
        'data-search-bar': DataSearchBar,
        'data-table-view': DataTableView,
        'csv-data-exporter': CsvDataExporter,
        'activity-filters': ActivityFilters,
        'filter-chips': FilterChips,
        'audit-logs-filters': AuditLogsFilters,
        'device-status-chart': DeviceStatusChart,
        'event-dialog': EventDialog
    },
    data() {
        return {
            title: 'Recent Activities',
            isDataLoading: false,
            exportDialogComponent: '',
            exportEntityName: '',
            exportParams: {},
            dataExportFields: [],
            dataExportFileName: '',
            auditLogsFilterChips: [],
            venueEventsFilterChips: [],
            showVenueEventsFilters: false,
            showAuditLogsFilters: false,
            tab: 0,
            refreshOtherTab: false,
            showEventDialog: false,
            selectedEvent: {},
            lastHealthPollDate: '',
            tabItems: [
                { name: 'auditLogs', label: 'Audit Logs' },
                { name: 'venueEvents', label: 'Venue Events' }
            ]
        };
    },
    computed: {
        ...mapState('activities', [
            'tableFields',
            'computedFields',
            'filterFields',
            'exportFields',
            'exportFileName',
            'requestQuery',
            'responseQuery',
            'filterQuery',
            'zebraStripping',
            'helpUrl'
        ]),
        ...mapState('activities', {
            venueEvents: 'items'
        }),
        ...mapState('auditLogs', {
            auditLogs: 'items',
            alTableFields: 'tableFields',
            alComputedFields: 'computedFields',
            alFilterFields: 'filterFields',
            alExportFields: 'exportFields',
            alExportFileName: 'exportFileName',
            alRequestQuery: 'requestQuery',
            alResponseQuery: 'responseQuery',
            alFilterQuery: 'filterQuery',
            alZebraStripping: 'zebraStripping'
        }),
        dataSearchBarOptions() {
            if (this.currentTab.name === 'auditLogs') {
                return {
                    page: this.alRequestQuery.number,
                    itemsPerPage: this.alRequestQuery.size,
                    pageCount: this.alResponseQuery.totalPages,
                    itemsLength: this.alResponseQuery.totalElements,
                    query: this.alRequestQuery.q
                };
            } else {
                return {
                    page: this.requestQuery.number,
                    itemsPerPage: this.requestQuery.size,
                    pageCount: this.responseQuery.totalPages,
                    itemsLength: this.responseQuery.totalElements,
                    query: this.requestQuery.q
                };
            }
        },
        sortBy() {
            if (this.currentTab.name === 'auditLogs') {
                return this.alRequestQuery.sort || '';
            } else {
                return this.requestQuery.sort;
            }
        },
        sortDesc() {
            if (this.currentTab.name === 'auditLogs') {
                return this.alRequestQuery.desc || false;
            } else {
                return this.requestQuery.desc;
            }
        },
        page() {
            if (this.currentTab.name === 'auditLogs') {
                return this.alRequestQuery.number || 1;
            } else {
                return this.requestQuery.number;
            }
        },
        itemsPerPage() {
            if (this.currentTab.name === 'auditLogs') {
                return this.alRequestQuery.size || 20;
            } else {
                return this.requestQuery.size;
            }
        },
        filters() {
            if (this.currentTab.name === 'auditLogs') {
                return this.alFilterQuery;
            } else {
                return this.filterQuery;
            }
        },
        isFiltersActive() {
            if (this.currentTab.name === 'auditLogs') {
                return !isEmptyValues(this.alFilterQuery);
            } else {
                return !isEmptyValues(this.filterQuery);
            }
        },
        showVenueEvents() {
            return this.$can('read', 'VenueEvent');
        },
        showAuditLogs() {
            return this.$can('read', 'Auditlogs');
        },
        currentTab() {
            return this.tabItems[this.tab];
        }
    },
    async created() {
        if (this.alTableFields && !this.alTableFields.length) {
            await this.alSetupView();
            await this.alResetRequestQuery();
            this.fetchAuditLogsOptions();
        }
        if (this.tableFields && !this.tableFields.length) {
            await this.setupView();
            await this.resetRequestQuery();
            this.fetchActivityOptions();
        }
    },
    async mounted() {
        this.isDataLoading = true;
        setTimeout(() => {
            this.findActivities(this.getInitialPageParams());
            this.getLastHealthPollDate();
            this.refreshOtherTab = true;
        }, 200);
    },
    methods: {
        // =================== Vuex Actions =====================
        ...mapActions('activities', [
            'fetchVenueEvents',
            'setupView',
            'resetRequestQuery',
            'fetchActivityOptions'
        ]),
        ...mapActions('auditLogs', {
            fetchAuditLogs: 'fetchAuditLogs',
            alSetupView: 'setupView',
            alResetRequestQuery: 'resetRequestQuery',
            fetchAuditLogsOptions: 'fetchAuditLogsOptions'
        }),
        ...mapMutations('activities', ['setRequestQuery']),
        ...mapMutations('auditLogs', { alSetRequestQuery: 'setRequestQuery' }),
        ...mapActions('devices', ['fetchLastHealthPollDate']),
        // ===================== Events ========================
        onDataSearchBarChange(options) {
            this.findActivities(
                {
                    ...options,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                this.filterQuery
            );
            this.updateOtherTabSearchOptions(options);
        },
        onUpdateSortBy(sortOptions) {
            if (sortOptions) {
                this.findActivities(
                    {
                        ...this.dataSearchBarOptions,
                        ...sortOptions
                    },
                    this.filterQuery
                );
            }
        },
        onExportDataClick() {
            const searchParams = {
                number: 1,
                size:
                    this.dataSearchBarOptions.itemsLength <= 10000
                        ? this.dataSearchBarOptions.itemsLength
                        : 10000,
                q: this.dataSearchBarOptions.query,
                sort: this.sortBy,
                desc: this.sortDesc
            };
            const filterParams = this.filterQuery;
            this.exportParams = {
                searchParams,
                filterParams,
                isDsSearch: false
            };
            this.exportEntityName =
                this.currentTab.name === 'auditLogs'
                    ? 'auditLogs'
                    : 'activities';
            this.dataExportFields =
                this.currentTab.name === 'auditLogs'
                    ? this.alExportFields
                    : this.exportFields;
            this.dataExportFileName =
                this.currentTab.name === 'auditLogs'
                    ? this.alExportFileName
                    : this.exportFileName;
            this.exportDialogComponent = 'csv-data-exporter';
        },
        onExportDialogClose() {
            this.exportDialogComponent = '';
        },
        onFilterClick() {
            if (this.currentTab.name === 'auditLogs') {
                this.showAuditLogsFilters = true;
            } else {
                this.showVenueEventsFilters = true;
            }
        },
        onApplyFilters({ filters, filtersDesc }) {
            this.applyFilters(filters, filtersDesc);
        },
        onFilterChipsChange(filtersDesc) {
            if (filtersDesc) {
                let newFilters = {};
                filtersDesc.forEach(item => {
                    newFilters[item.name] = item.value;
                });
                this.applyFilters(newFilters, filtersDesc);
            }
        },
        async onTabChange() {
            if (this.refreshOtherTab) {
                await this.findActivities(
                    {
                        ...this.dataSearchBarOptions,
                        sortBy: this.sortBy,
                        sortDesc: this.sortDesc
                    },
                    this.filterQuery
                );
                this.refreshOtherTab = false;
            }
        },
        onVenueEventClick(item, event) {
            this.selectedEvent = item;
            this.showEventDialog = true;
            this.lastFocusedElement = event.target;
        },
        onCloseEvent() {
            this.selectedEvent = {};
            this.showEventDialog = false;
        },
        onRefreshClick() {
            const filtersDesc =
                this.currentTab.name === 'venueEvents'
                    ? this.venueEventsFilterChips
                    : this.auditLogsFilterChips;
            this.applyFilters(this.filters, filtersDesc);
            this.getLastHealthPollDate();
        },
        // ===================== Others ========================
        applyFilters(filters, filtersDesc) {
            if (this.currentTab.name === 'auditLogs') {
                this.auditLogsFilterChips = filtersDesc;
            } else {
                this.venueEventsFilterChips = filtersDesc;
            }
            this.findActivities(
                {
                    ...this.dataSearchBarOptions,
                    page: 1,
                    sortBy: this.sortBy,
                    sortDesc: this.sortDesc
                },
                filters
            );
        },
        async findActivities(searchOptions, filterOptions) {
            this.isDataLoading = true;
            try {
                if (!isEmpty(searchOptions)) {
                    const searchParams = {
                        number: searchOptions.page,
                        size: searchOptions.itemsPerPage,
                        q: searchOptions.query,
                        sort: searchOptions.sortBy,
                        desc: searchOptions.sortDesc
                    };
                    const filterParams = { ...filterOptions };
                    if (this.currentTab.name === 'auditLogs') {
                        await this.fetchAuditLogs({
                            searchParams,
                            filterParams
                        });
                    } else {
                        await this.fetchVenueEvents({
                            searchParams,
                            filterParams
                        });
                    }
                }
            } finally {
                this.isDataLoading = false;
            }
        },
        getInitialPageParams() {
            return {
                page: 1,
                itemsPerPage: this.itemsPerPage,
                query: this.requestQuery.q,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };
        },
        updateOtherTabSearchOptions(options) {
            if (
                this.currentTab.name === 'auditLogs' &&
                options.query !== this.requestQuery.q
            ) {
                this.setRequestQuery({
                    ...this.requestQuery,
                    q: options.query
                });
                this.refeshOtherTab = true;
            } else if (
                this.currentTab.name === 'venueEvents' &&
                options.query !== this.alRequestQuery.q
            ) {
                this.alSetRequestQuery({
                    ...this.alRequestQuery,
                    q: options.query
                });
                this.refreshOtherTab = true;
            }
        },
        getLastHealthPollDate() {
            this.fetchLastHealthPollDate({}).then(res => {
                if (res) {
                    this.lastHealthPollDate = new Date(res).toLocaleString();
                }
            });
        }
    }
};
</script>

<style scoped lang="scss">
::v-deep .scrollable-content.v-tabs-items {
    .v-window__container {
        height: 100%;
    }
    .v-window-item {
        height: 100%;
    }
}
::v-deep .scrollable-container {
    > .v-tabs {
        flex-grow: 0;
    }
}
::v-deep .v-tabs.thin-tabs {
    .v-tabs-bar {
        height: 35px;
    }
}
</style>
